// Copyright (c) 2022 by Move Closer

import { computed, onMounted, ref, toRef, watch } from '@vue/composition-api'

import { canBeDisabledProp, useSyncModel } from '../../composables'
import { ComponentObjectPropsOptions, Emit } from '../../vue-api'

import { StarsRateProps } from './StarsRate.contracts'

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export const starsRateProps: ComponentObjectPropsOptions<StarsRateProps> = {
  ...canBeDisabledProp,

  /**
   * @see StarsRateProps.double
   */
  allowHalfValues: {
    type: Boolean,
    required: false,
    default: false
  },

  /**
   * @see StarsRateProps.isBlack
   */
  isBlack: {
    type: Boolean,
    required: false,
    default: false
  },

  /**
   * @see StarsRateProps.isLarge
   */
  isLarge: {
    type: Boolean,
    required: false,
    default: false
  },

  /**
   * @see StarsRateProps.model
   */
  model: {
    type: [Number, Object]
  },

  /**
   * @see StarsRateProps.stars
   */
  rate: {
    type: Number
  },

  /**
   * @see StarsRateProps.hasForm
   */
  hasForm: {
    type: Boolean,
    required: false,
    default: true
  }
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export const useStarsRate = (props: StarsRateProps, emit: Emit) => {
  const id: number = Math.floor(Math.random() * 100000)

  const checked = useSyncModel(toRef(props, 'model'), emit)
  const rounded = ref(checked.value)

  function calculateValue (): void {
    const decimalInt = Math.trunc(checked.value)
    const fraction = checked.value - Math.floor(checked.value)

    if (fraction < 0.25) {
      rounded.value = decimalInt
    }

    if (fraction >= 0.25 && fraction <= 0.75) {
      rounded.value = decimalInt + 0.5
    }

    if (fraction > 0.75) {
      rounded.value = decimalInt + 1
    }
  }

  const component = computed<string>(() => {
    if (props.hasForm) {
      return 'fieldset'
    } else {
      return 'div'
    }
  })

  /**
   * Creates an array of rating values.
   * We need to reverse the array because of inverse flex of elements.
   */
  const starsCount = computed<number[]>(() => {
    if (Number(props.model) > props.rate) {
      throw new Error(`Given value is more than applicable rate! Given rate: [${props.model}]`)
    }

    if (props.rate < 0) {
      throw new Error(`Negative value given as rate count! Got - ${[props.rate]}`)
    }

    if (!props.allowHalfValues) {
      return Array.from(Array(props.rate).keys()).reverse()
    }

    return Array.from({ length: (props.rate) / 0.5 }, (_, index) => {
      return ((index + 1) * 0.5)
    }).reverse()
  })

  onMounted(() => {
    calculateValue()
  })

  watch(() => checked.value, () => {
    calculateValue()
  })

  watch<number>(() => rounded.value, (value, oldValue) => {
    if (value === oldValue) return

    checked.value = value
    emit('chosen', Number(value))
  })

  return { checked, component, id, rounded, starsCount }
}
